import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  BUByLocationDto,
  BusinessUnitInterface,
  ExchangeRateDto,
  GeneralParams,
  PieceCategoryInterface,
  RangeDeclareDto,
  TaxIdentificationTypeInterface,
} from '../../interfaces'
import { LocationDto } from '../../interfaces/Dto/LocationDto'
import { NavigationItem } from '../../components'
import { AppDataInterface } from '../../interfaces/AppDataInterface'

interface InmutableState {
  appData: AppDataInterface
  cities: LocationDto[]
  // Ubicaciones de todos los países (sin filtrar por CountryId) — para orígenes.
  originCities: LocationDto[]
  businessUnits: BusinessUnitInterface[]
  PieceCategories: PieceCategoryInterface[]
  taxIdentificationTypes: TaxIdentificationTypeInterface[]
  citiesByBu: BUByLocationDto[]
  rangeDeclareList: RangeDeclareDto[]
  exchangeRate: ExchangeRateDto
  navSidebar: NavigationItem[]
  volumenMin: GeneralParams[]
  densityMax: GeneralParams[]
  highDeclaredValue: GeneralParams
  token: string | null
  tokenExpiration: number | null
  isRefreshing: boolean
}

interface TokenPayload {
  token: string
  expiresIn: number
}

const initialState: InmutableState = {
  appData: {} as AppDataInterface,
  cities: [],
  originCities: [],
  businessUnits: [],
  PieceCategories: [],
  taxIdentificationTypes: [],
  citiesByBu: [],
  rangeDeclareList: [],
  exchangeRate: {} as ExchangeRateDto,
  navSidebar: [],
  volumenMin: [],
  densityMax: [],
  highDeclaredValue: {} as GeneralParams,
  token: null,
  tokenExpiration: null,
  isRefreshing: false,
}

export const inmutableSlice = createSlice({
  name: 'inmutable',
  initialState,
  reducers: {
    setInmutableAppData: (state, action: PayloadAction<AppDataInterface>) => {
      state.appData = action.payload
    },
    setInmutableTaxIdentificationTypes: (
      state,
      action: PayloadAction<TaxIdentificationTypeInterface[]>
    ) => {
      state.taxIdentificationTypes = action.payload
    },
    setInmutablePieceCategories: (
      state,
      action: PayloadAction<PieceCategoryInterface[]>
    ) => {
      state.PieceCategories = action.payload
    },
    setInmutableBusinessUnits: (
      state,
      action: PayloadAction<BusinessUnitInterface[]>
    ) => {
      state.businessUnits = action.payload
    },
    setInmutableCities: (state, action: PayloadAction<LocationDto[]>) => {
      state.cities = action.payload
    },
    setInmutableOriginCities: (
      state,
      action: PayloadAction<LocationDto[]>
    ) => {
      state.originCities = action.payload
    },
    setInmutableCitiesByBu: (
      state,
      action: PayloadAction<BUByLocationDto[]>
    ) => {
      state.citiesByBu = action.payload
    },
    setInmutableRangeDeclareList: (
      state,
      action: PayloadAction<RangeDeclareDto[]>
    ) => {
      state.rangeDeclareList = action.payload
    },
    setInmutableExchangeRate: (
      state,
      action: PayloadAction<ExchangeRateDto>
    ) => {
      state.exchangeRate = action.payload
    },
    setNavigationItemList: (state, action: PayloadAction<NavigationItem[]>) => {
      state.navSidebar = action.payload
    },
    setVolumenMin: (state, action: PayloadAction<GeneralParams[]>) => {
      state.volumenMin = action.payload
    },
    setDensityMax: (state, action: PayloadAction<GeneralParams[]>) => {
      state.densityMax = action.payload
    },
    setHighDeclaredValue: (state, action: PayloadAction<GeneralParams>) => {
      state.highDeclaredValue = action.payload
    },
    setToken: (state, action: PayloadAction<TokenPayload>) => {
      state.token = action.payload.token
      // Calcula la fecha de expiración (timestamp en milisegundos)
      state.tokenExpiration = Date.now() + action.payload.expiresIn * 1000
    },
    clearToken: (state) => {
      state.token = null
      state.tokenExpiration = null
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.isRefreshing = action.payload
    },
  },
})

export const {
  setInmutableAppData,
  setInmutableCities,
  setInmutableOriginCities,
  setInmutableBusinessUnits,
  setInmutablePieceCategories,
  setInmutableTaxIdentificationTypes,
  setInmutableCitiesByBu,
  setInmutableRangeDeclareList,
  setInmutableExchangeRate,
  setNavigationItemList,
  setVolumenMin,
  setDensityMax,
  setHighDeclaredValue,
  setToken,
  clearToken,
  setRefreshing,
} = inmutableSlice.actions

export default inmutableSlice.reducer
